/**
 * Phase 17 — admin offer composer.
 *
 * Renders the form to set an admin offer (final price + optional
 * discount + expiration). Submits via `setAdminOffer`; on success
 * passes the updated Estimate back up. 409 conflicts surface as
 * "version mismatch — reload and retry".
 */

import { useState } from "react";

import { setAdminOffer } from "@starz/shared/api/sdk.gen";
import type { Estimate } from "@starz/shared/api/types.gen";

interface Props {
  estimate: Estimate;
  /** Default total used to pre-fill `final_cents` — typically the
   * snapshot's `subtotal_high_cents` or the previous offer's
   * `final_cents`. */
  defaultFinalCents: number;
  onSaved: (updated: Estimate) => void;
}

function isoLocalDefault(hoursAhead: number): string {
  const d = new Date(Date.now() + hoursAhead * 60 * 60 * 1000);
  // input[type=datetime-local] wants `YYYY-MM-DDTHH:MM`.
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AdminOfferComposer({ estimate, defaultFinalCents, onSaved }: Props) {
  const [finalDollars, setFinalDollars] = useState(
    String((defaultFinalCents / 100).toFixed(2)),
  );
  const [discountDollars, setDiscountDollars] = useState("");
  const [expiresLocal, setExpiresLocal] = useState(isoLocalDefault(14));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const final = Math.round(parseFloat(finalDollars) * 100);
    if (!Number.isFinite(final) || final < 0) {
      setError("Final price must be a non-negative number.");
      return;
    }
    let discount: number | null = null;
    if (discountDollars.trim()) {
      const d = Math.round(parseFloat(discountDollars) * 100);
      if (!Number.isFinite(d) || d < 0) {
        setError("Discount must be a non-negative number.");
        return;
      }
      if (d > final) {
        setError("Discount can't exceed the final price.");
        return;
      }
      discount = d;
    }
    const expiresAt = new Date(expiresLocal).toISOString();
    setBusy(true);
    const result = await setAdminOffer({
      estimateId: estimate.estimate_id,
      ifMatchVersion: estimate.version,
      body: {
        final_cents: final,
        discount_cents: discount,
        expires_at: expiresAt,
      },
    });
    setBusy(false);
    if ("error" in result) {
      setError(
        result.error.error.code === "CONFLICT"
          ? "Estimate changed elsewhere — reload and retry."
          : result.error.error.message,
      );
      return;
    }
    onSaved(result.data);
  }

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-3 rounded-lg border border-starz-gold/20 bg-starz-cream/40 p-4"
    >
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
            Final price ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={finalDollars}
            onChange={(e) => setFinalDollars(e.target.value)}
            className="mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
            Discount ($, optional)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={discountDollars}
            onChange={(e) => setDiscountDollars(e.target.value)}
            className="mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
            Expires
          </label>
          <input
            type="datetime-local"
            value={expiresLocal}
            onChange={(e) => setExpiresLocal(e.target.value)}
            className="mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      {error ? (
        <p className="border-l-2 border-starz-brick pl-3 text-xs text-starz-brick">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-starz-navy px-4 py-1.5 text-xs font-semibold text-starz-off-white hover:opacity-90 disabled:opacity-40"
        >
          {busy ? "Saving…" : "Set offer"}
        </button>
      </div>
    </form>
  );
}
